import { configureStore, type Middleware, type Reducer } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';
import type { PharmacySelectorState } from 'app/shared-components/store/pharmacySelectorSlice';
import createReducer from './rootReducer.ts';

type AsyncReducersMap = Record<string, Reducer>;

const middlewares: Middleware[] = [];

if (process.env.NODE_ENV === 'development') {
  const logger = createLogger({
    collapsed: (_getState: unknown, _action: unknown, logEntry: { error?: unknown }) =>
      !logEntry.error,
  });
  middlewares.push(logger);
}

const store = configureStore({
  reducer: createReducer(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }).concat(middlewares),
  devTools: process.env.NODE_ENV === 'development',
});

type StoreWithAsyncReducers = typeof store & { asyncReducers: AsyncReducersMap };
const storeWithAsyncReducers = store as StoreWithAsyncReducers;
storeWithAsyncReducers.asyncReducers = {};

export type RootState = TypedRootState;
export type AppDispatch = typeof store.dispatch;

// Option A: explicitly type the slice(s) we care about now.
// This prevents `useSelector` consumers from collapsing to `any` while the rest of the store is still JS / loosely typed.
export type TypedRootState = {
  pharmacySelector: PharmacySelectorState;
} & Record<string, unknown>;

export const injectReducer = (key: string, reducer: Reducer): false | typeof store => {
  if (storeWithAsyncReducers.asyncReducers[key]) {
    return false;
  }

  storeWithAsyncReducers.asyncReducers[key] = reducer;
  store.replaceReducer(createReducer(storeWithAsyncReducers.asyncReducers));
  return store;
};

export default store;
